import { sortCurrencies } from 'components/modals/expenses/sortCurrencies';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetMattersListQuery } from 'store/api/matters';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { ExpenseForm } from 'types/expenses';
import { FeatureFlag } from 'types/features';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { partyNumberString } from 'utils/partyNameUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';

const USED_CLASSIFIERS = ['CURRENCY', 'EXPENSE_TYPE'];

export const EXPENSE_FORM_DEFAULTS: ExpenseForm = {
    externalId: null,
    expenseType: null,
    currencyType: null,
    description: '',
    expenseAmount: '',
    expenseDate: formatDateStringInput(new Date()),
    isNonBillable: false,
    employee: null,
    matter: null,
    timecardId: null,
    effortId: null,
};

interface Props {
    /** The values to show; `undefined` while a new expense's defaults are still loading. */
    initialData: ExpenseForm | undefined;
    isEdit: boolean;
}

/**
 * The expense form's state, shared by the create and edit pages and by the record's sections: the form, its
 * option lists, and the case's hold on the non-billable box.
 */
export const useExpenseForm = ({ initialData, isEdit }: Props) => {
    const isNegativeAmountAllowed = useIsFeatureEnabled(FeatureFlag.EXPENSES_NEGATIVE_AMOUNT_ALLOWED);
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });
    const { data: tenant, isLoading: isLoadingTenant } = useGetCurrentTenantQuery();

    const methods = useForm<ExpenseForm>({
        defaultValues: EXPENSE_FORM_DEFAULTS,
        values: initialData,
    });
    const { control, setValue } = methods;

    // `useWatch` doesn't reflect the matter injected via the `values` prop on edit (it only tracks
    // setValue-driven changes), so seed it with the initial matter. Without this, `currentMatter`
    // is undefined on edit and the non-billable lock below never engages.
    const currentMatter = useWatch({ control, name: 'matter', defaultValue: initialData?.matter ?? undefined });

    const { data: currentMatterQueryData } = useGetMattersListQuery(
        { pageSize: 1, page: 0, filters: `id==${currentMatter?.id}` },
        { skip: !currentMatter?.id }
    );
    // RTK Query keeps the previous request's data while the next matter loads; ignore it until it
    // matches the selected matter so a billable->non-billable switch can't lock isNonBillable on
    // (or prioritize currencies) from the wrong case.
    const fetchedMatter = currentMatterQueryData?.content.at(0);
    const currentMatterData = fetchedMatter?.id === currentMatter?.id ? fetchedMatter : undefined;

    // A non-billable case forces the expense non-billable (enforced server-side), so lock the box and
    // auto-check it for new expenses. Prefer the freshly fetched matter, falling back to the
    // selected/initial value while that query is in flight.
    const isMatterNonBillable = currentMatterData?.isNonBillable ?? currentMatter?.isNonBillable ?? false;

    // The form only stores a minimal matter (no clientParty), so the client comes from the matter
    // fetched above. Same rule for the dropdown's group heading and the client line under the field,
    // so the two stay in step.
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const selectedClientParty = currentMatterData?.clientParty;
    const selectedClientName = selectedClientParty
        ? [partyNumberString(selectedClientParty, isPartyNumberUsageEnabled), selectedClientParty.name]
              .filter(Boolean)
              .join(' ')
        : null;

    // On edit the checkbox must show the stored value, not the matter-derived one: expenses that
    // predate their case turning non-billable still hold false, and auto-checking would misreport
    // the row. Auto-check only for new expenses or when the user switches the case to a non-billable
    // one, where the checked box previews what the save will store.
    const hasMatterChanged = currentMatter?.id !== initialData?.matter?.id;

    useEffect(() => {
        if (isMatterNonBillable && (!isEdit || hasMatterChanged)) {
            setValue('isNonBillable', true);
        } else if (isEdit && !hasMatterChanged) {
            setValue('isNonBillable', initialData?.isNonBillable ?? false);
        }
    }, [isMatterNonBillable, setValue, isEdit, hasMatterChanged, initialData]);

    const currencyOptions = useMemo(() => {
        const customCurrencyPriority = [
            currentMatterData?.pricelistCurrencyTypeCode,
            tenant?.baseCurrencyTypeCode,
        ].filter((code): code is string => typeof code === 'string');
        return (
            (getValidOptionsFromClassifier(classifiers['CURRENCY']) ?? []) as { label: string; value: string }[]
        ).toSorted((a, b) => sortCurrencies(a, b, customCurrencyPriority));
    }, [classifiers, currentMatterData?.pricelistCurrencyTypeCode, tenant?.baseCurrencyTypeCode]);

    const expenseTypeOptions = getValidOptionsFromClassifier(classifiers['EXPENSE_TYPE']) ?? [];

    return {
        methods,
        classifiers,
        isLoading: isLoadingClassifiers || isLoadingTenant,
        isNegativeAmountAllowed,
        isPartyNumberUsageEnabled,
        isMatterNonBillable,
        selectedClientName,
        currencyOptions,
        expenseTypeOptions,
    };
};

export type ExpenseFormState = ReturnType<typeof useExpenseForm>;
